import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@d12labs/ui/select";
import { cn } from "@d12labs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronRight, Download, Plus, TrendingUp, Users } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";
import { AffiliationMenu } from "../components/affiliation-menu";
import { CompareRadar } from "../components/compare-radar";
import { Delta } from "../components/delta";
import { GoalBar } from "../components/goal-bar";
import { StreakBadge } from "../components/streak-badge";
import { TREND_SERIES, TrendChart, type TrendSeriesKey } from "../components/trend-chart";
import { computeStreaks } from "../streaks";
import {
	CATEGORY_KEYS,
	CATEGORY_LABELS,
	type CategoryKey,
	overallScoreColor,
	type SpeakerTrends,
} from "../types";

// ── helpers ────────────────────────────────────────────────────────────────────

const shortDate = (iso: string) =>
	new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const relativeDate = (iso: string) => {
	const d = new Date(iso);
	const now = new Date();
	const diffMs = now.getTime() - d.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	if (diffDays === 0) return "today";
	if (diffDays < 7) return `${diffDays}d ago`;
	if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
	return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

// ── component ──────────────────────────────────────────────────────────────────

export function SpeakerProfilePage() {
	const { id } = useParams({ strict: false }) as { id: string };
	const queryClient = useQueryClient();
	const [category, setCategory] = useState<CategoryKey>("clarity");
	const [target, setTarget] = useState("80");
	// "Set goal" header action scrolls the goals card into view.
	const goalsRef = useRef<HTMLDivElement>(null);
	const scrollToGoals = () =>
		goalsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

	// Which lines the trend chart shows — overall on by default, categories opt-in.
	const [activeSeries, setActiveSeries] = useState<TrendSeriesKey[]>(["overall"]);
	const toggleSeries = (key: TrendSeriesKey) =>
		setActiveSeries((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
		);

	// Comparison pickers (analysis ids); null falls back to first vs. last.
	const [compareA, setCompareA] = useState<string | null>(null);
	const [compareB, setCompareB] = useState<string | null>(null);

	const trends = useQuery({
		queryKey: ["meeting-perf", "trends", id],
		// `affiliation` is an untyped text column on the wire row; the route's Zod
		// enum is the app-level constraint — narrow to the `SpeakerTrends` union here.
		queryFn: () => meetingPerfApi.speakerTrends({ params: { id } }) as Promise<SpeakerTrends>,
	});
	const goals = useQuery({
		queryKey: ["meeting-perf", "goals", id],
		queryFn: () => meetingPerfApi.listGoals({ params: { id } }),
	});
	// Used only to label history rows with their meeting title.
	const meetingsList = useQuery({
		queryKey: ["meeting-perf", "meetings", "titles"],
		queryFn: () => meetingPerfApi.listMeetings({ query: { pageSize: 100 } }),
	});

	// Persisted internal/external classification (D12-63), settable from the header.
	const setAffiliation = useMutation({
		mutationFn: (affiliation: "internal" | "external" | null) =>
			meetingPerfApi.setAffiliation({ params: { id }, input: { affiliation } }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["meeting-perf", "trends", id] });
			queryClient.invalidateQueries({ queryKey: ["meeting-perf", "speakers"] });
			toast.success("Affiliation updated");
		},
		onError: (err) => toast.error((err as Error).message),
	});

	const addGoal = useMutation({
		mutationFn: (payload: { category: CategoryKey; targetScore: number }) =>
			meetingPerfApi.createGoal({ params: { id }, input: payload }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["meeting-perf", "goals", id] });
			toast.success("Goal added");
		},
		onError: (err) => toast.error((err as Error).message),
	});

	// Trend series from the query (empty while loading).
	const trendSeries = trends.data?.series ?? [];

	const titleById = useMemo(
		() => new Map((meetingsList.data?.items ?? []).map((m) => [m.id, m.title])),
		[meetingsList.data],
	);

	// Loading / error states — before the "no data" early returns (hooks must be top-level).
	if (trends.isLoading) {
		return (
			<>
				{/* 2px primary top-bar during load */}
				<div className="h-0.5 w-full bg-primary" />
				<PageHeader eyebrow="MEETING PERFORMANCE" icon={Users} title="Loading…" />
				<PageBody className="p-6">
					<p className="text-body-sm text-ink-subtle">Loading…</p>
				</PageBody>
			</>
		);
	}
	if (trends.error || !trends.data) {
		return (
			<>
				<PageHeader eyebrow="MEETING PERFORMANCE" icon={Users} title="Not found" />
				<PageBody className="p-6">
					<p className="text-body-sm text-destructive">Speaker not found.</p>
				</PageBody>
			</>
		);
	}

	const { speaker, series } = trends.data;
	const latest = series.at(-1);
	const first = series[0];

	// KPI strip — derived from the UNFILTERED series.
	const peak = series.reduce((m, p) => Math.max(m, p.overallScore), 0);
	const avg = series.length
		? Math.round(series.reduce((s, p) => s + p.overallScore, 0) / series.length)
		: 0;
	const lift = latest && first ? latest.overallScore - first.overallScore : 0;

	// Resolved comparison points: default Baseline=first, Compare=last.
	const baselinePoint = series.find((p) => p.analysisId === compareA) ?? series[0];
	const comparePoint = series.find((p) => p.analysisId === compareB) ?? series.at(-1);

	const streaks = computeStreaks(series);

	// Goals: count met/total.
	const goalsData = goals.data ?? [];
	const metCount = goalsData.filter((g) => g.achieved).length;

	// Only let the user set goals for categories that don't already have one.
	const usedCategories = new Set(goalsData.map((g) => g.category as CategoryKey));
	const availableCategories = CATEGORY_KEYS.filter((k) => !usedCategories.has(k));
	// The Select's value must stay valid as goals are added — fall back to the
	// first still-available category without a reset effect.
	const effectiveCategory = availableCategories.includes(category)
		? category
		: (availableCategories[0] ?? category);

	// Subtitle for PageHeader.
	const trackedSince = speaker.createdAt ? relativeDate(speaker.createdAt) : null;
	const subtitleParts = [
		speaker.isPrimary ? "Primary speaker" : "Tracked speaker",
		trackedSince ? `tracked since ${trackedSince}` : null,
		series.length > 0 ? `${series.length} ${series.length === 1 ? "analysis" : "analyses"}` : null,
	].filter(Boolean);

	// Radar inputs: 6 scores in CATEGORY_KEYS order, 0–10 scale.
	const toRadarScores = (point: typeof baselinePoint): number[] =>
		CATEGORY_KEYS.map((k) => point?.categories[k] ?? 0);

	return (
		<>
			<PageHeader
				eyebrow="MEETING PERFORMANCE"
				icon={Users}
				title={speaker.name}
				subtitle={subtitleParts.join(" · ")}
				badges={speaker.isPrimary ? <Badge tone="primary">You</Badge> : undefined}
				actions={
					<>
						<AffiliationMenu
							value={speaker.affiliation}
							onSet={(affiliation) => setAffiliation.mutate(affiliation)}
						/>
						<Button variant="ghost" size="sm">
							<Download className="mr-1.5 h-3.5 w-3.5" />
							Export
						</Button>
						<Button variant="secondary" size="sm" onClick={scrollToGoals}>
							<Plus className="mr-1.5 h-3.5 w-3.5" />
							Set goal
						</Button>
					</>
				}
			/>

			<PageBody className="overflow-y-auto px-6 py-5 space-y-5">
				{series.length === 0 ? (
					<div className="rounded-lg border border-hairline bg-surface-1">
						<EmptyState
							icon={TrendingUp}
							title="No trend yet"
							body={`We need at least one analysis to plot a trend. Analyze ${speaker.name} in a recent meeting to start tracking improvement.`}
							primary={
								<Link to="/meeting-perf">
									<Button>Pick a meeting</Button>
								</Link>
							}
						/>
					</div>
				) : (
					<>
						{/* ── Streak badges ── */}
						{streaks.length > 0 ? (
							<div className="flex flex-wrap gap-1.5">
								{streaks.map((s) => (
									<StreakBadge key={s.id} streak={s} />
								))}
							</div>
						) : null}

						{/* ── KPI strip ── */}
						{latest ? (
							<div className="grid grid-cols-4 gap-3">
								{/* Latest */}
								<div className="rounded-lg border border-hairline bg-surface-1 p-4">
									<div className="text-eyebrow uppercase text-ink-tertiary">Latest</div>
									<div
										className={cn(
											"mt-1 text-[28px] font-semibold tabular-nums leading-none",
											overallScoreColor(latest.overallScore),
										)}
									>
										{latest.overallScore}
									</div>
									<div className="mt-1 text-meta text-ink-subtle">
										{shortDate(latest.meetingDate)}
									</div>
								</div>

								{/* Peak */}
								<div className="rounded-lg border border-hairline bg-surface-1 p-4">
									<div className="text-eyebrow uppercase text-ink-tertiary">Peak</div>
									<div
										className={cn(
											"mt-1 text-[28px] font-semibold tabular-nums leading-none",
											overallScoreColor(peak),
										)}
									>
										{peak}
									</div>
									<div className="mt-1 text-meta text-ink-subtle">Best to date</div>
								</div>

								{/* Average */}
								<div className="rounded-lg border border-hairline bg-surface-1 p-4">
									<div className="text-eyebrow uppercase text-ink-tertiary">Average</div>
									<div
										className={cn(
											"mt-1 text-[28px] font-semibold tabular-nums leading-none",
											overallScoreColor(avg),
										)}
									>
										{avg}
									</div>
									<div className="mt-1 text-meta text-ink-subtle">
										Across {series.length} {series.length === 1 ? "meeting" : "meetings"}
									</div>
								</div>

								{/* Lift */}
								<div className="rounded-lg border border-hairline bg-surface-1 p-4">
									<div className="text-eyebrow uppercase text-ink-tertiary">Lift</div>
									<div className="mt-1 text-[28px] font-semibold leading-none">
										<Delta value={lift} />
									</div>
									<div className="mt-1 text-meta text-ink-subtle">Since first analysis</div>
								</div>
							</div>
						) : null}

						{/* ── Score trend card (height-capped) ── */}
						<div className="rounded-lg border border-hairline bg-surface-1">
							{/* Card header */}
							<div className="flex items-center justify-between border-b border-hairline px-4 py-3">
								<h2 className="card-title text-ink">Score trend</h2>
								<span className="text-meta text-ink-subtle">Categories shown on 0–100 (×10)</span>
							</div>

							<div className="px-4 py-3">
								{/* Category chips */}
								<div className="mb-3 flex flex-wrap items-center gap-1.5">
									{TREND_SERIES.map((s) => {
										const on = activeSeries.includes(s.key);
										return (
											<button
												key={s.key}
												type="button"
												onClick={() => toggleSeries(s.key)}
												className={cn(
													"flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-body-sm transition-colors",
													on
														? "border-hairline-strong bg-surface-2 text-ink"
														: "border-hairline bg-transparent text-ink-subtle hover:bg-surface-1 hover:text-ink",
												)}
											>
												<span
													className="h-2 w-2 rounded-full"
													style={{
														backgroundColor: on ? s.color : "var(--color-hairline-strong)",
													}}
												/>
												{s.label}
											</button>
										);
									})}
								</div>

								{/* Height-capped chart */}
								<div className="h-[200px] overflow-hidden">
									<TrendChart series={trendSeries} active={activeSeries} />
								</div>
							</div>
						</div>

						{/* ── Two-column body ── */}
						<div className="grid grid-cols-[1fr_360px] gap-4 items-start">
							{/* LEFT — Compare two meetings */}
							<div className="rounded-lg border border-hairline bg-surface-1">
								{/* Card header + selectors */}
								<div className="flex flex-col gap-3 border-b border-hairline px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
									<div>
										<h2 className="card-title text-ink">Compare two meetings</h2>
										<p className="mt-0.5 text-meta text-ink-subtle">
											Overlay any two analyses to see where the lift came from.
										</p>
									</div>

									{series.length >= 2 ? (
										<div className="flex items-end gap-3">
											{/* Baseline selector */}
											<div className="flex flex-col gap-1">
												<span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-tertiary">
													Baseline
												</span>
												<Select value={baselinePoint?.analysisId ?? ""} onValueChange={setCompareA}>
													<SelectTrigger className="h-7 min-w-[140px] text-body-sm">
														<span className="flex items-center gap-1.5">
															<span className="h-2 w-2 rounded-full bg-[var(--color-ink-tertiary)]" />
															<SelectValue />
														</span>
													</SelectTrigger>
													<SelectContent>
														{series.map((p) => (
															<SelectItem key={p.analysisId} value={p.analysisId}>
																{shortDate(p.meetingDate)} · {p.overallScore}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>

											{/* Compare selector */}
											<div className="flex flex-col gap-1">
												<span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-tertiary">
													Compare
												</span>
												<Select value={comparePoint?.analysisId ?? ""} onValueChange={setCompareB}>
													<SelectTrigger className="h-7 min-w-[140px] text-body-sm">
														<span className="flex items-center gap-1.5">
															<span className="h-2 w-2 rounded-full bg-[var(--color-primary)]" />
															<SelectValue />
														</span>
													</SelectTrigger>
													<SelectContent>
														{series.map((p) => (
															<SelectItem key={p.analysisId} value={p.analysisId}>
																{shortDate(p.meetingDate)} · {p.overallScore}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
										</div>
									) : null}
								</div>

								{/* Compare body */}
								{series.length < 2 ? (
									<div className="flex items-center justify-center px-4 py-10">
										<p className="text-body-sm text-ink-subtle">Needs 2+ analyses to compare.</p>
									</div>
								) : baselinePoint && comparePoint ? (
									<div className="grid grid-cols-[260px_1fr] gap-4 p-4">
										{/* Radar */}
										<div className="flex items-center justify-center">
											<CompareRadar
												baseline={toRadarScores(baselinePoint)}
												compare={toRadarScores(comparePoint)}
												labels={CATEGORY_KEYS.map((k) => CATEGORY_LABELS[k])}
												size={240}
											/>
										</div>

										{/* Delta table */}
										<div>
											{/* Table header */}
											<div className="grid grid-cols-[1fr_52px_52px_52px] gap-x-2 border-b border-hairline pb-2 text-eyebrow uppercase text-ink-tertiary">
												<span>KPI</span>
												<span className="text-right">{shortDate(baselinePoint.meetingDate)}</span>
												<span className="text-right">{shortDate(comparePoint.meetingDate)}</span>
												<span className="text-right">Δ</span>
											</div>

											{/* Overall row */}
											<div className="grid grid-cols-[1fr_52px_52px_52px] gap-x-2 items-baseline border-b border-hairline py-2 text-body-sm font-medium text-ink">
												<span>Overall</span>
												<span className="text-right tabular-nums text-ink-subtle">
													{baselinePoint.overallScore}
												</span>
												<span className="text-right tabular-nums">{comparePoint.overallScore}</span>
												<span className="text-right">
													<Delta value={comparePoint.overallScore - baselinePoint.overallScore} />
												</span>
											</div>

											{/* Per-dimension rows */}
											{CATEGORY_KEYS.map((key) => {
												const av = baselinePoint.categories[key] ?? 0;
												const bv = comparePoint.categories[key] ?? 0;
												return (
													<div
														key={key}
														className="grid grid-cols-[1fr_52px_52px_52px] gap-x-2 items-baseline border-t border-hairline/60 py-2 text-body-sm text-ink-muted"
													>
														<span>{CATEGORY_LABELS[key]}</span>
														<span className="text-right tabular-nums text-ink-subtle">{av}</span>
														<span className="text-right tabular-nums">{bv}</span>
														<span className="text-right">
															<Delta value={bv - av} />
														</span>
													</div>
												);
											})}
										</div>
									</div>
								) : null}
							</div>

							{/* RIGHT rail */}
							<div className="space-y-4">
								{/* Goals card */}
								<div ref={goalsRef} className="rounded-lg border border-hairline bg-surface-1 p-5">
									<div className="mb-3 flex items-start justify-between">
										<div className="flex flex-col gap-0.5">
											<h2 className="card-title text-ink">Goals</h2>
											<span className="text-meta text-ink-subtle">
												Targets across the six KPIs.
											</span>
										</div>
										{goalsData.length > 0 ? (
											<Badge tone="subtle">
												{metCount} / {goalsData.length}
											</Badge>
										) : null}
									</div>

									<div className="flex flex-col">
										{goalsData.length > 0 ? (
											goalsData.map((g, i) => {
												const current = latest?.categories[g.category as CategoryKey];
												// Category scores are 0–10; goals are stored 0–100 → compare on same scale.
												const current100 = current != null ? current * 10 : null;
												return (
													<div key={g.id} className={i ? "border-t border-hairline" : undefined}>
														<GoalBar
															label={CATEGORY_LABELS[g.category as CategoryKey] ?? g.category}
															current={current100}
															target={Number(g.targetScore)}
															achieved={g.achieved}
														/>
													</div>
												);
											})
										) : (
											<p className="py-2 text-body-sm text-ink-subtle">No goals set yet.</p>
										)}
									</div>

									{availableCategories.length === 0 ? (
										<p className="mt-4 border-t border-hairline pt-4 text-body-sm text-ink-subtle">
											All six goals are set.
										</p>
									) : (
										<form
											className="mt-4 flex items-end gap-2 border-t border-hairline pt-4"
											onSubmit={(e) => {
												e.preventDefault();
												const targetScore = Number(target);
												if (!Number.isFinite(targetScore) || targetScore < 0 || targetScore > 100) {
													toast.error("Target must be between 0 and 100");
													return;
												}
												addGoal.mutate({ category: effectiveCategory, targetScore });
											}}
										>
											<div className="flex flex-1 flex-col gap-1.5">
												<span className="text-meta text-ink-subtle">Category</span>
												<Select
													value={effectiveCategory}
													onValueChange={(v) => setCategory(v as CategoryKey)}
												>
													<SelectTrigger>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{availableCategories.map((key) => (
															<SelectItem key={key} value={key}>
																{CATEGORY_LABELS[key]}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
											<div className="flex w-20 flex-col gap-1.5">
												<span className="text-meta text-ink-subtle">Target</span>
												<Input
													type="number"
													min={0}
													max={100}
													value={target}
													onChange={(e) => setTarget(e.target.value)}
												/>
											</div>
											<Button type="submit" disabled={addGoal.isPending}>
												<Plus className="h-4 w-4" /> Add
											</Button>
										</form>
									)}
								</div>

								{/* History card */}
								<div className="rounded-lg border border-hairline bg-surface-1">
									<div className="flex items-center justify-between border-b border-hairline px-4 py-3">
										<h2 className="card-title text-ink">History</h2>
										<span className="text-meta text-ink-subtle">
											{series.length} {series.length === 1 ? "analysis" : "analyses"}
										</span>
									</div>
									<div className="flex flex-col">
										{[...series].reverse().map((p, i) => (
											<Link
												key={p.analysisId}
												to="/meeting-perf/meetings/$id"
												params={{ id: p.meetingId }}
												className={cn(
													"flex items-center gap-3 px-4 py-2.5 text-body-sm transition-colors hover:bg-surface-2/40",
													i > 0 ? "border-t border-hairline" : undefined,
												)}
											>
												<span className="w-12 flex-none font-mono text-meta tabular-nums text-ink-subtle">
													{shortDate(p.meetingDate)}
												</span>
												<span className="flex-1 truncate text-body-sm text-ink-muted">
													{titleById.get(p.meetingId) ?? "Meeting analysis"}
												</span>
												<span
													className={cn(
														"tabular-nums text-body-sm font-medium",
														overallScoreColor(p.overallScore),
													)}
												>
													{p.overallScore}
													<span className="text-meta text-ink-subtle">/100</span>
												</span>
												<ChevronRight className="h-3.5 w-3.5 flex-none text-ink-subtle" />
											</Link>
										))}
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</PageBody>
		</>
	);
}
